import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Divider, Tooltip, Chip, IconButton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Link as RouterLink } from 'react-router-dom';
import type { ClientInfo } from '../../api/request_api';
import type { FHRequest } from '../../types';
import { useAuthStore } from '../../stores/root_store';
import { StatusChip } from '../StatusChip';
import { aiApi, type TriageResult } from '../../api/ai_api';

interface RequestDetailsCardProps {
  request: FHRequest;
  clientInfo: ClientInfo | null;
  loading?: boolean;
}

export function RequestDetailsCard({
  request,
  clientInfo,
}: RequestDetailsCardProps) {
  const authStore = useAuthStore();
  const isManager = authStore.roles.includes('manager');
  const isAssistant = authStore.isAssistant;
  const [incomingEmailCopied, setIncomingEmailCopied] = useState(false);
  const [clientEmailCopied, setClientEmailCopied] = useState(false);
  const [triage, setTriage] = useState<TriageResult | null>(null);

  useEffect(() => {
    if (request.uuid) {
      aiApi.getTriage(request.uuid)
        .then((res) => setTriage(res.triage))
        .catch(() => setTriage(null));
    }
  }, [request.uuid]);

  const handleCopyIncomingEmail = async () => {
    if (request.incoming_email) {
      try {
        await navigator.clipboard.writeText(request.incoming_email);
        setIncomingEmailCopied(true);
        setTimeout(() => setIncomingEmailCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy email:', err);
      }
    }
  };

  const handleCopyClientEmail = async () => {
    if (clientInfo?.primary_email) {
      try {
        await navigator.clipboard.writeText(clientInfo.primary_email);
        setClientEmailCopied(true);
        setTimeout(() => setClientEmailCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy email:', err);
      }
    }
  };

  const getClientDisplayName = () => {
    if (clientInfo?.first_name && clientInfo?.last_name) {
      return `${clientInfo.first_name} ${clientInfo.last_name}`;
    }
    if (clientInfo?.first_name) return clientInfo.first_name;
    if (clientInfo?.name) return clientInfo.name;
    return 'Client';
  };

  const getClientLocation = () => {
    const parts = [];
    if (clientInfo?.home_city) parts.push(clientInfo.home_city);
    if (clientInfo?.home_state) parts.push(clientInfo.home_state);
    if (parts.length === 0 && clientInfo?.work_city)
      parts.push(clientInfo.work_city);
    if (parts.length === 0 && clientInfo?.work_state)
      parts.push(clientInfo.work_state);
    return parts.join(', ') || null;
  };

  const getFullAddress = (type: 'home' | 'work') => {
    if (!clientInfo) return null;
    const parts = [];
    if (type === 'home') {
      if (clientInfo.home_city) parts.push(clientInfo.home_city);
      if (clientInfo.home_state) parts.push(clientInfo.home_state);
      if (clientInfo.home_zip) parts.push(clientInfo.home_zip);
    } else {
      if (clientInfo.work_city) parts.push(clientInfo.work_city);
      if (clientInfo.work_state) parts.push(clientInfo.work_state);
    }
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">Request Details</Typography>
        </Box>

        {/* Status */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <StatusChip status={request.status} />
          {request.archived && (
            <Chip label="Archived" size="small" color="default" />
          )}
        </Box>

        {/* Category Chips */}
        {triage && triage.category_details.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {triage.category_details.map((cat) => (
              <Tooltip key={cat.id} title={cat.description}>
                <Chip
                  label={cat.name}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              </Tooltip>
            ))}
          </Box>
        )}

        {/* Client Info Section */}
        {clientInfo && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Name */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <PersonIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
              <Box>

                {isManager && clientInfo?.key ? (
                  <Typography
                    variant="body2"
                    component={RouterLink}
                    to={`/manager/user/${clientInfo.key}`}
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {getClientDisplayName()}
                  </Typography>
                ) : (
                  <Typography variant="body2">{getClientDisplayName()}</Typography>
                )}
              </Box>
            </Box>

            {/* Email */}
            {clientInfo.primary_email && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <EmailIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {clientInfo.primary_email}
                    </Typography>
                    <Tooltip title={clientEmailCopied ? 'Copied!' : 'Copy email'}>
                      <IconButton
                        size="small"
                        onClick={handleCopyClientEmail}
                        sx={{ p: 0.25 }}
                      >
                        {clientEmailCopied ? (
                          <CheckIcon fontSize="small" color="success" />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Phone */}
            {clientInfo.phone_number && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <PhoneIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">{clientInfo.phone_number}</Typography>
                </Box>
              </Box>
            )}

            {/* Location */}
            {getClientLocation() && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2">{getClientLocation()}</Typography>
                </Box>
              </Box>
            )}

            {/* Home Address */}
            {getFullAddress('home') && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Home
                  </Typography>
                  <Typography variant="body2">{getFullAddress('home')}</Typography>
                </Box>
              </Box>
            )}

            {/* Work Address */}
            {getFullAddress('work') && getFullAddress('work') !== getFullAddress('home') && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Work
                  </Typography>
                  <Typography variant="body2">{getFullAddress('work')}</Typography>
                </Box>
              </Box>
            )}

            {/* Timezone */}
            {clientInfo.timezone_city && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AccessTimeIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Timezone
                  </Typography>
                  <Typography variant="body2">{clientInfo.timezone_city}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Team Info - Assistant and Mentor */}
        {(request.assistant_name || request.mentor_name) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {request.assistant_name && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <SupportAgentIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    {isManager && request.assistant_uuid ? (
                      <Typography
                        variant="body2"
                        component={RouterLink}
                        to={`/manager/user/${request.assistant_uuid}`}
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {request.assistant_name}
                      </Typography>
                    ) : (
                      <Typography variant="body2">{request.assistant_name}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Assistant
                    </Typography>
                  </Box>

                </Box>
              )}

              {request.mentor_name && (isManager || isAssistant) && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <SupervisorAccountIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    {isManager && request.mentor_uuid ? (
                      <Typography
                        variant="body2"
                        component={RouterLink}
                        to={`/manager/user/${request.mentor_uuid}`}
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {request.mentor_name}
                      </Typography>
                    ) : (
                      <Typography variant="body2" >{request.mentor_name}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Mentor
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Request Dates */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Price
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                ${Number(request.price).toFixed(2)}
                {Number(request.task_credits) > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({request.task_credits} credit{request.task_credits !== 1 ? 's' : ''})
                  </Typography>
                )}
              </Typography>
              {request.solver_paid && (
                <Tooltip title={request.solver_paid_date ? new Date(request.solver_paid_date).toLocaleString() : 'Paid'}>
                  <Chip label="Paid" size="small" color="success" />
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Request Email */}
          {request.incoming_email && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Incoming Email
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {request.incoming_email}
                </Typography>
                <Tooltip title={incomingEmailCopied ? 'Copied!' : 'Copy email'}>
                  <IconButton
                    size="small"
                    onClick={handleCopyIncomingEmail}
                    sx={{ p: 0.25 }}
                  >
                    {incomingEmailCopied ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">
              {new Date(request.date_created).toLocaleString()}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Last Updated
            </Typography>
            <Typography variant="body2">
              {new Date(request.date_updated).toLocaleString()}
            </Typography>
          </Box>

          {/* Member Since */}
          {clientInfo?.date_created && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Member Since
              </Typography>
              <Typography variant="body2">
                {new Date(clientInfo.date_created).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
